/**
 * Grocery showroom — no-login persona entries + the bottom-center switcher.
 * Same structure as HotelDemoSwitcher / AutoDemoSwitcher, ready for Store and
 * Driver in a later phase. An entry RESUMES an existing demo session and never
 * overwrites one.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, RotateCcw, ShoppingBasket, User, Baby, Store, Users, Sun, Moon, SunMoon } from 'lucide-react';
import LanguageToggle from '../LanguageToggle';
import { useGroceryMode } from '../../lib/groceryTheme';
import { useAnySheetOpen } from '../../lib/phoneSheet';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
export const GROCERY_DEMO_ORG_ID = 'grocery-demo-org-0001';
export const GROCERY_DEMO_CARDS = { claimed: 'GROCERYDEMO-0001', unclaimed: 'GROCERYDEMO-0002' };
const key = (cardId) => `yulontap_grocery:${cardId}`;

/** Enter a persona. The SERVER session is what decides who you are on the
 *  list (the engine reads `session_member_id`), so switching persona must
 *  switch the session — storing a member id beside a stale Parent session was
 *  why /demo/grocery/household could still show the Parent. The household is
 *  the same either way, so nothing of the demo story is lost. */
export async function enterGroceryPersona(persona) {
    const r = await fetch(`${API}/api/demo-grocery/${persona}/session`);
    if (!r.ok) throw new Error(`http_${r.status}`);
    const d = await r.json();
    if (!d?.claimed_card || !d?.session) throw new Error('no_session');
    try {
        localStorage.setItem(key(d.claimed_card),
            JSON.stringify({ session: d.session, member_id: d.member.id }));
        sessionStorage.setItem('yulontap_grocery_demo', '1');
    } catch (_e) { /* private mode */ }
    return d;
}

/** THE DEMO WAS RESET IN ANOTHER TAB. Every call then fails on a session that
 *  no longer exists, so this tab quietly takes a new one and reloads. One
 *  short line says so once the list is back. Only ever on a demo card. */
let recovering = false;
export async function recoverDemoSession() {
    if (recovering) return false;
    let onDemo = false;
    try { onDemo = sessionStorage.getItem('yulontap_grocery_demo') === '1'; } catch (_e) { /* ignore */ }
    if (!onDemo) return false;
    recovering = true;
    try {
        const d = await enterGroceryPersona('customer');
        sessionStorage.setItem('yulontap_grocery_refreshed', '1');
        window.location.replace(`/grocery/${d.claimed_card}`);
        return true;
    } catch (_e) {
        recovering = false;
        return false;
    }
}

/** Reads and clears the "say it once" flag set just before the reload. */
export function consumeDemoRefreshed() {
    try {
        if (sessionStorage.getItem('yulontap_grocery_refreshed') !== '1') return false;
        sessionStorage.removeItem('yulontap_grocery_refreshed');
        return true;
    } catch (_e) { return false; }
}

function Entry({ persona, testId }) {
    const { t } = useTranslation();
    const [failed, setFailed] = useState(false);
    const go = async () => {
        setFailed(false);
        try {
            const d = await enterGroceryPersona(persona);
            window.location.replace(`/grocery/${d.claimed_card}`);
        } catch (_e) { setFailed(true); }
    };
    useEffect(() => { go(); /* eslint-disable-next-line */ }, [persona]);
    return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3" data-testid={testId}>
            {failed ? (
                <>
                    <p className="text-sm font-semibold text-foreground" data-testid="demo-grocery-entry-failed">
                        {t('grocery.couldNotLoad')}
                    </p>
                    <button onClick={go} data-testid="demo-grocery-entry-retry"
                        className="min-h-[44px] rounded-full bg-emerald-700 px-5 text-sm font-bold text-white">
                        {t('grocery.tryAgain')}
                    </button>
                </>
            ) : (
                <>
                    <Loader2 className="w-7 h-7 animate-spin text-emerald-500" />
                    <p className="text-sm text-muted-foreground">{t('grocery.demo.entering')}</p>
                </>
            )}
        </div>
    );
}

export const DemoGroceryCustomerEntry = () => <Entry persona="customer" testId="demo-grocery-customer-entry" />;
export const DemoGroceryHouseholdEntry = () => <Entry persona="household" testId="demo-grocery-household-entry" />;

export function shouldShowGroceryDemoSwitcher(pathname) {
    if (pathname.startsWith('/demo/grocery/store')) return true;
    if (typeof window === 'undefined') return false;
    let flag = false;
    try { flag = sessionStorage.getItem('yulontap_grocery_demo') === '1'; } catch (_e) { /* ignore */ }
    const onDemoCard = Object.values(GROCERY_DEMO_CARDS).some((c) => pathname.includes(`/grocery/${c}`));
    return onDemoCard || (flag && pathname.startsWith('/grocery'));
}

export default function GroceryDemoSwitcher() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    // A SHEET OWNS THE SCREEN. While the chef window, "Add to list", "Who's
    // adding" or "Send my list" is open this bar steps out of the way: on a
    // phone it sat exactly where the typing row is and it was taking the taps.
    const sheetOpen = useAnySheetOpen();
    const onDemo = shouldShowGroceryDemoSwitcher(pathname);
    // While the bar is on screen the list keeps room underneath itself, so the
    // billing, share and order cards are never half under it.
    useEffect(() => {
        const root = document.documentElement;
        if (onDemo) root.classList.add('yot-demo-bar');
        else root.classList.remove('yot-demo-bar');
        return () => root.classList.remove('yot-demo-bar');
    }, [onDemo]);

    if (!onDemo || sheetOpen) return null;

    const go = async (persona) => {
        setBusy(true);
        try {
            const d = await enterGroceryPersona(persona);
            navigate(`/grocery/${d.claimed_card}`, { replace: true });
            window.location.reload();
        } catch (_e) {
            setBusy(false);
            toast.error(t('grocery.couldNotLoad'));
        }
    };

    const reset = async () => {
        setBusy(true);
        try {
            const r = await fetch(`${API}/api/demo-grocery/reset`, { method: 'POST' });
            if (!r.ok) throw new Error(`http_${r.status}`);
            const d = await r.json();
            // Clear every saved list id, or a stale tab looks broken after a reset.
            try {
                (d.clear_storage_prefixes || ['yulontap_grocery:']).forEach((p) => {
                    Object.keys(localStorage).filter((k) => k.startsWith(p))
                        .forEach((k) => localStorage.removeItem(k));
                });
            } catch (_e) { /* ignore */ }
            await enterGroceryPersona('customer');
            navigate(`/grocery/${d.claimed_card}`, { replace: true });
            window.location.reload();
        } catch (_e) {
            setBusy(false);
            toast.error(t('grocery.couldNotLoad'));
        }
    };

    const CustomersPicker = () => {
        const [open, setOpen] = useState(false);
        const [rows, setRows] = useState([]);
        const [q, setQ] = useState('');
        useEffect(() => {
            if (!open || rows.length) return;
            fetch(`${(typeof window !== 'undefined' ? window.location.origin : '')}/api/demo-grocery/customers`)
                .then((r) => r.json()).then((d) => setRows(d.customers || []))
                .catch(() => setRows([]));
        }, [open, rows.length]);
        const needle = q.trim().toLowerCase();
        const shown = rows.filter((r) => !needle
            || `${r.card_id} ${r.name} ${r.unit}`.toLowerCase().includes(needle));
        return (
            <>
                <Chip icon={Users} label={t('grocery.demo.customers')}
                    testId="grocery-demo-customers" onClick={() => setOpen((v) => !v)} />
                {open ? createPortal((
                    <div className="fixed left-1/2 z-[110] -translate-x-1/2 w-[min(94vw,26rem)] max-h-[60vh] overflow-y-auto rounded-2xl border border-white/10 bg-slate-900/95 p-3 shadow-2xl backdrop-blur-md"
                        style={{ bottom: 'calc(5rem + var(--yot-safe-bottom))' }}
                        data-testid="grocery-demo-customers-panel">
                        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus
                            placeholder={t('grocery.demo.searchCustomers')}
                            data-testid="grocery-demo-customers-search"
                            className="mb-2 min-h-[44px] w-full rounded-xl bg-white/10 px-3 text-sm text-white placeholder:text-white/40" />
                        <p className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-400"
                            data-testid="grocery-demo-customers-count">
                            {shown.length} / {rows.length}
                        </p>
                        {shown.map((r) => (
                            <button key={r.card_id} data-testid={`grocery-demo-customer-${r.card_id}`}
                                onClick={() => {
                                    // A chosen household opens on ITS OWN card. We must not
                                    // mint the baseline persona here or every pick would land
                                    // back on Stacey's list.
                                    try {
                                        Object.keys(localStorage)
                                            .filter((k) => k.startsWith('yulontap_grocery:'))
                                            .forEach((k) => localStorage.removeItem(k));
                                    } catch (_e) { /* ignore */ }
                                    window.location.assign(`/grocery/${r.card_id}`);
                                }}
                                className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-xl px-2 text-left text-sm text-white/90 hover:bg-white/10">
                                <span className="truncate"><b>{r.card_id}</b> · {r.name}</span>
                                <span className="shrink-0 text-xs text-white/50">
                                    {t('grocery.demo.unitShort')} {r.unit} · {r.language.toUpperCase()}
                                </span>
                            </button>
                        ))}
                    </div>
                ), document.body) : null}
            </>
        );
    };

    /** The same mode the header control drives — one piece of state, two views,
     *  so flipping it in the demo bar is exactly what a customer would see. */
    const ModeChip = () => {
        const { mode, cycle } = useGroceryMode();
        const Icon = { auto: SunMoon, day: Sun, night: Moon }[mode] || SunMoon;
        return <Chip icon={Icon} label={t(`grocery.mode.${mode}`)} onClick={cycle}
            testId="grocery-demo-mode" />;
    };

    const Chip = ({ icon: Icon, label, onClick, testId }) => (
        <button onClick={onClick} disabled={busy} data-testid={testId}
            className="shrink-0 whitespace-nowrap flex items-center gap-1.5 px-3 min-h-[44px] rounded-full text-xs font-semibold text-white/90 hover:text-white hover:bg-white/10 transition-colors">
            <Icon className="w-4 h-4" />{label}
        </button>
    );

    return (
        <div className="fixed left-1/2 -translate-x-1/2 z-40 max-w-[calc(100vw-1rem)]"
            style={{ bottom: 'calc(1rem + var(--yot-safe-bottom))' }}
            data-launcher-avoid="lift"
            data-testid="grocery-demo-switcher">
            {/* A scroller with no edge looks like a cut-off bar. */}
            <span aria-hidden className="pointer-events-none absolute right-0 top-0 h-full w-8 rounded-r-full bg-gradient-to-l from-slate-900/95 to-transparent" />
            <div className="flex flex-nowrap items-center gap-1 rounded-full bg-slate-900/85 backdrop-blur-md border border-white/10 px-2 py-1 shadow-xl max-w-[calc(100vw-1rem)] overflow-x-auto no-scrollbar">
                <span className="shrink-0 whitespace-nowrap flex items-center gap-1.5 px-2 text-xs font-bold text-emerald-400">
                    <ShoppingBasket className="w-4 h-4" />
                    {/* On a phone the word is dropped, not the chips: a reset
                        chip nobody can see is worse than a missing label. */}
                    <span className="hidden sm:inline">{t('grocery.demo.label')}</span>
                </span>
                <Chip icon={User} label={t('grocery.demo.parent')} onClick={() => go('customer')} testId="grocery-demo-parent" />
                <Chip icon={Baby} label={t('grocery.demo.kid')} onClick={() => go('household')} testId="grocery-demo-kid" />
                <CustomersPicker />
                <ModeChip />
                <Chip icon={Store} label={t('grocery.demo.store')} testId="grocery-demo-store"
                    onClick={() => navigate('/demo/grocery/store')} />
                <Chip icon={busy ? Loader2 : RotateCcw} label={t('grocery.demo.reset')} onClick={reset} testId="grocery-demo-reset" />
                <Chip icon={ShoppingBasket} label={t('grocery.demo.newCard')} testId="grocery-demo-unclaimed"
                    onClick={() => navigate(`/grocery/${GROCERY_DEMO_CARDS.unclaimed}`)} />
                <div className="pl-1 shrink-0"><LanguageToggle /></div>
            </div>
        </div>
    );
}
